"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type SidebarState = "expanded" | "collapsed" | "hidden";

interface LayoutContextType {
  sidebarState: SidebarState;
  setSidebarState: (state: SidebarState) => void;
  isMobile: boolean;
  isTablet: boolean;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [sidebarState, setSidebarState] = useState<SidebarState>("expanded");
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);

      if (width < 768) {
        setSidebarState("hidden");
      } else if (width < 1024) {
        setSidebarState("collapsed");
      } else {
        setSidebarState("expanded");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <LayoutContext.Provider value={{ sidebarState, setSidebarState, isMobile, isTablet }}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within LayoutProvider");
  }
  return context;
}
